import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { BatchReturn, BatchReturnStatusType } from 'returnCenter-api';
import { useTranslation } from 'react-i18next';
import { getBatchStatsTranslations } from 'utils/helpers';
import styled from 'styled-components';

type BatchReturnHeadDetailsProps = {
    batchReturn: BatchReturn;
};

const BatchReturnHeadDetails = ({ batchReturn }: BatchReturnHeadDetailsProps) => {
    const { t } = useTranslation();

    const statusTranslation = getBatchStatsTranslations(t, batchReturn.status);

    return (
        <DetailWrapper column className="batch-return-head-details" gap="1rem">
            <TitleWrapper align="start" justify="between" gap="0.5rem">
                <Title lineheight={40} padding={0} margin={0} fontSize={48} as="h1">
                    {t('batchReturn.detail', 'Batch Return')}
                    <Text fontSize={40} thin as="span">
                        {` #${batchReturn.id}`}
                    </Text>
                </Title>
                <StatusBadge align="center" gap="0.5rem">
                    <StatusDot $status={batchReturn.status} />
                    <Text thin fontSize={22} padding="0" margin="0" as="h2">
                        {statusTranslation}
                    </Text>
                </StatusBadge>
            </TitleWrapper>

            <MetaGrid>
                <MetaItem column>
                    <Text as="label" fontSize={11} xBold>
                        {t('batchReturn.destination', 'Destination')}
                    </Text>
                    <Text margin="0" fontSize={13}>
                        {batchReturn.batchDestinationName ?? '-'}
                    </Text>
                </MetaItem>
                <MetaItem column>
                    <Text as="label" fontSize={11} xBold>
                        {t('batchReturn.createdBy', 'Created by')}
                    </Text>
                    <Text margin="0" fontSize={13}>
                        {batchReturn.createdByUser ?? '-'}
                    </Text>
                </MetaItem>
                <MetaItem column>
                    <Text as="label" fontSize={11} xBold>
                        {t('batchReturn.created', 'Created')}
                    </Text>
                    <Text margin="0" fontSize={13}>
                        {batchReturn.created ? new Date(batchReturn.created).toLocaleDateString() : '-'}
                    </Text>
                </MetaItem>
                {batchReturn.closedDate && (
                    <MetaItem column>
                        <Text as="label" fontSize={11} xBold>
                            {t('batchReturn.closedBy', 'Closed by')}
                        </Text>
                        <Text margin="0" fontSize={13}>
                            {batchReturn.closedByUser ?? '-'} ({new Date(batchReturn.closedDate).toLocaleDateString()})
                        </Text>
                    </MetaItem>
                )}
                {batchReturn.sentDate && (
                    <MetaItem column>
                        <Text as="label" fontSize={11} xBold>
                            {t('batchReturn.sentBy', 'Sent by')}
                        </Text>
                        <Text margin="0" fontSize={13}>
                            {batchReturn.sentByUser ?? '-'} ({new Date(batchReturn.sentDate).toLocaleDateString()})
                        </Text>
                    </MetaItem>
                )}
                {batchReturn.comment && (
                    <MetaItem column>
                        <Text as="label" fontSize={11} xBold>
                            {t('batchReturn.comment', 'Comment')}
                        </Text>
                        <Text margin="0" fontSize={13}>
                            {batchReturn.comment}
                        </Text>
                    </MetaItem>
                )}
            </MetaGrid>
        </DetailWrapper>
    );
};

export default BatchReturnHeadDetails;

const DetailWrapper = styled(Flex)`
    background: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    padding: 1rem;
`;

const TitleWrapper = styled(Flex)`
    flex-wrap: wrap;
`;

const Title = styled(Text)`
    font-weight: 500;
`;

const StatusBadge = styled(Flex)``;

const StatusDot = styled.span<{ $status?: BatchReturnStatusType }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ $status }) => {
        switch ($status) {
            case BatchReturnStatusType.Open:
                return '#4CAF50';
            case BatchReturnStatusType.Closed:
                return '#FF9800';
            case BatchReturnStatusType.Sent:
                return '#2196F3';
            case BatchReturnStatusType.Received:
                return '#9C27B0';
            case BatchReturnStatusType.PartiallyProcessed:
                return '#607D8B';
            case BatchReturnStatusType.Completed:
                return '#4CAF50';
            default:
                return '#b6b3b3';
        }
    }};
`;

const MetaGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-top: 1rem;
`;

const MetaItem = styled(Flex)`
    min-width: 120px;
`;
